import { useState } from "react";
import { Alert, Badge, Button, Card, Grid, Group, Stack, TagsInput, Text, Title } from "@mantine/core";
import { IconHeartbeat, IconInfoCircle } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { FoodConditionCheckResult, KitchenToolRequest } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { AiContextFields } from "../components/AiContextFields";
import { MediaInput, type MediaInputValue } from "../components/MediaInput";
import { BulletSection, CONDITION_OPTIONS, KitchenPending, NutritionChips } from "../components/kitchen";
import { composeMessage } from "../lib/clientSettings";
import { kitchenRequest } from "../lib/kitchen";
import { SAMPLE_PHOTOS } from "../lib/samples";

type Verdict = FoodConditionCheckResult["verdicts"][number]["verdict"];

const VERDICTS: Record<Verdict, { label: string; color: string }> = {
  good_fit: { label: "Good fit", color: "lime.8" },
  ok_in_moderation: { label: "OK in moderation", color: "yellow.7" },
  limit: { label: "Limit", color: "orange" },
  avoid: { label: "Avoid", color: "red" },
  unknown: { label: "Not sure", color: "gray" },
};

const EXAMPLES: { text: string; conditions: string[] }[] = [
  { text: "A large slice of pepperoni pizza and a regular cola", conditions: ["type 2 diabetes"] },
  { text: "Spicy chicken curry with tomatoes and onions, plus a coffee", conditions: ["GERD / acid reflux"] },
  { text: "A deli turkey sandwich with pickles and a bag of chips", conditions: ["high blood pressure", "chronic kidney disease"] },
];

export function ConditionCheckPage() {
  const [input, setInput] = useState<MediaInputValue>({ text: "", media: null });
  const [conditions, setConditions] = useState<string[]>(["type 2 diabetes"]);
  const [note, setNote] = useState("");
  const [last, setLast] = useState<KitchenToolRequest | null>(null);
  const m = useMutation({ mutationFn: (req: KitchenToolRequest) => passio.tools.foodConditionCheck(req) });

  const submit = (text = input.text, conds = conditions) => {
    if (!text.trim() && !input.media) return;
    setInput({ ...input, text });
    setConditions(conds);
    const req = kitchenRequest({ text, media: input.media, conditions: conds, message: composeMessage(note) });
    setLast(req);
    m.mutate(req);
  };
  const data = m.data?.data;

  return (
    <RequireFeature feature="health">
      <PageHeader
        eyebrow="POST /napi/tools/foodConditionCheck"
        title="Food vs. health conditions"
        description="How well a food or meal fits one or more health conditions: what matters in this portion, a practical portion size, and safer swaps. General information with a fixed disclaimer, not medical advice."
        actions={<RouteModelPicker routes={["foodConditionCheck"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <TagsInput label="Conditions" data={CONDITION_OPTIONS} value={conditions} onChange={setConditions} placeholder="Pick or type" />
              <MediaInput value={input} onChange={setInput} textPlaceholder="Describe the food or meal and the portion…" photoLabel="Drop a photo of the food, plate or label" samples={SAMPLE_PHOTOS} />
              <AiContextFields note={note} onNoteChange={setNote} placeholder="e.g. I'm on metformin" />
              <Button leftSection={<IconHeartbeat size={16} />} onClick={() => submit()} loading={m.isPending} disabled={!input.text.trim() && !input.media} size="md">
                Check it
              </Button>
              {!input.media && (
                <Stack gap={6}>
                  <Text fz="xs" c="dimmed">
                    Examples
                  </Text>
                  {EXAMPLES.map((ex) => (
                    <Text key={ex.text} fz="sm" c="violet.7" style={{ cursor: "pointer" }} onClick={() => submit(ex.text, ex.conditions)}>
                      “{ex.text}” · {ex.conditions.join(", ")}
                    </Text>
                  ))}
                </Stack>
              )}
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => last && m.mutate(last)} />}
          {m.isPending && <KitchenPending title="Checking against your conditions…" />}
          {data && !m.isPending && (
            <Stack gap="md">
              <ResultHeader title={data.foodName || "Result"} meta={m.data?.meta} feedback={<FeedbackButtons requestId={m.data?.meta.requestId} />} />
              <Card>
                <Stack gap="xs">
                  {data.identifiedFrom && (
                    <Text fz="xs" c="dimmed">
                      Based on: {data.identifiedFrom}
                    </Text>
                  )}
                  <NutritionChips n={data.estimatedNutrition} label="AI estimate for this portion" />
                  {data.overallSummary && <Text>{data.overallSummary}</Text>}
                </Stack>
              </Card>
              {data.verdicts.map((v, i) => {
                const meta = VERDICTS[v.verdict] ?? VERDICTS.unknown;
                return (
                  <Card key={i} style={{ borderLeft: `4px solid var(--mantine-color-${meta.color.replace(".", "-")})` }}>
                    <Group gap="xs" mb={6}>
                      <Title order={3} fz={18} tt="capitalize">
                        {v.condition}
                      </Title>
                      <Badge color={meta.color} variant="filled">
                        {meta.label}
                      </Badge>
                    </Group>
                    <Stack gap="sm">
                      {v.summary && <Text fz="sm">{v.summary}</Text>}
                      <BulletSection title="What matters" items={v.concerns} color="orange" />
                      <BulletSection title="In this portion" items={v.keyNutrients} />
                      {v.portionGuidance && (
                        <Text fz="sm">
                          <b>Portion:</b> {v.portionGuidance}
                        </Text>
                      )}
                      <BulletSection title="Better swaps" items={v.saferSwaps} color="lime.8" />
                    </Stack>
                  </Card>
                );
              })}
              <Alert color="gray" variant="light" icon={<IconInfoCircle size={18} />}>
                <Text fz="xs">{data.disclaimer}</Text>
              </Alert>
            </Stack>
          )}
          {!data && !m.isPending && !m.error && <EmptyState title="Check a food">Add one or more conditions, then a photo or description with the portion.</EmptyState>}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
